import { Injectable } from '@angular/core';

import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApiEnum, UserFunctionsEnum } from '../enumerators/enumerators.enum';
import { AlertEnum, DialogEnum, OptionEnum } from '../enumerators/messages.enum';
import { DialogOption } from '../models/dialog-option.model';
import { MenuOption } from '../models/menu-option.model';
import { ServiceResponse } from "../models/service-response.model";
import { User } from '../models/user.model';
import { ConfigService } from './app-config.service';
import { AppService } from './app.service';
import { MessageService } from './message.service';
import { SystemService } from './system.service';

@Injectable()
export class UserService {
    urlUsuario: string = '';
    urlUsuarioAdmin: string = '';
    urlIdioma: string;
    urlLanguages: string = '';
    urlParams: string = '';
    urlSession: string = '';
    logeando: boolean = false;

    timerSesion: any;
    sesionFin: any;

    constructor(private appService: AppService, private configService: ConfigService, private messageService: MessageService, private systemService: SystemService, public translate: TranslateService, private router: Router) {
        if (localStorage.getItem(this.systemService.localStoreVariable.JWT) && !localStorage.getItem(this.systemService.localStoreVariable.AOT)) {
            localStorage.removeItem(this.systemService.localStoreVariable.JWT);
        }

        this.urlIdioma = this.configService.config.securityAPI + 'configuracion';
        this.urlLanguages = this.configService.config.securityAPI + 'languages';
        this.urlParams = this.configService.config.securityAPI + 'params';
        this.setAPI();

        this.appService.logOut.subscribe((url) => {
            if (url && !url.includes('/usuario/cerrarsesion')) {
                this.logOut();
            }
        });
    }

    // sesion
    logIn(datos: any)
    {
        this.logeando = true;

        this.appService.post(this.urlUsuario, datos).then((response: ServiceResponse) =>
        {
            let data = response.data;
            localStorage.setItem(this.systemService.localStoreVariable.JWT, data.token);
            localStorage.setItem(this.systemService.localStoreVariable.AOT, this.appService.decrypt(data.aot));
            localStorage.setItem(this.systemService.localStoreVariable.Servicios, this.appService.decrypt(data.servicios));
            let tema: string | null = localStorage.getItem('tema');
            if (!tema) { localStorage.setItem('tema', 'md-light-indigo.css'); }
            localStorage.removeItem(this.systemService.localStoreVariable.LenguajeCambiado);

            this.setAPI();
            this.router.navigateByUrl('/app/home');
            this.logeando = false;
        }).catch((error) =>
        {
          this.messageService.alert(AlertEnum.Error, error.message);
          this.logeando = false;
        });
    }

    logOut() {
        this.appService.put(this.urlUsuario + '/log-out', null);
        this.logOutConfirmed();
    }

    // Método específico para cerrar sesión por expiración (sin petición al backend)
    logOutByExpiration() {
        this.logOutConfirmed();
    }

    logOutConfirmed() {
        // Limpiar todos los timers de sesión
        if (this.timerSesion) {
            clearInterval(this.timerSesion);
            this.timerSesion = null;
        }

        // Limpiar variables del localStorage relacionadas con la sesión
        localStorage.removeItem(this.systemService.localStoreVariable.JWT);
        localStorage.removeItem(this.systemService.localStoreVariable.AOT);
        localStorage.removeItem(this.systemService.localStoreVariable.Servicios);
        localStorage.removeItem(this.systemService.localStoreVariable.SesionValida);
        localStorage.removeItem(this.systemService.localStoreVariable.UsuarioEnmascarado);
        localStorage.removeItem(this.systemService.localStoreVariable.LenguajeCambiado);

        // Resetear estado de la aplicación
        this.appService.noAutorizado = false;
        this.systemService.clearSessionData();
        this.systemService.loadedInitialData = false; // Reset para permitir nueva carga

        // Notificar que la sesión ha terminado (para limpiar UI)
        this.appService.sessionEnded.next(true);

        // Resetear configuración de API
        this.setAPI();

        // Navegar al login
        this.router.navigateByUrl('/auth/login');
    }

    logOutMask()
    {
        this.appService.put(this.urlUsuario + '/logout-mask', null).then((response: ServiceResponse) =>
        {
            window.location.reload();
        });
    }

    resetUserPassword(usuario: User) {
        return new Promise((resolve) => {
            this.appService.put(this.urlUsuarioAdmin + '/reset-password', usuario).then((reponse : ServiceResponse) =>
            {
              this.messageService.alert(AlertEnum.Success, this.translate.instant('OperacionExitosa'));
              return resolve(true);
            });
        });
    }

    // datos
    loadInitialData() {
        return new Promise((resolve, reject) => {
            // Evitar cargar datos múltiples veces
            if (this.systemService.loadedInitialData) {
                return resolve(1);
            }

            // Marcar como cargando para evitar llamadas simultáneas
            this.systemService.loadedInitialData = true;

            if (this.systemService.languages.length == 0) {
                this.appService.get(this.urlLanguages + '?Filters=active%3D%3Dtrue').then((response : ServiceResponse) =>
                  {
                    this.systemService.languages = response.data;
                    this.systemService.setLanguages();

                    if (this.systemService.parameters.length == 0) {
                      this.loadParameters().then((response: any) => {
                        return resolve(1);
                      });
                    } else {
                      return resolve(1);
                    }
                  }
                ).catch(() =>
              {
                this.systemService.menuOptions = [];
                this.systemService.loadedInitialData = false; // Reset en caso de error
                return resolve(0);
              });
            } else {
                if (this.systemService.parameters.length == 0) {
                    this.loadParameters().then((response: any) => {
                        return resolve(1);
                    });
                } else {
                    return resolve(1);
                }
            }
        }).catch(() => {
            this.systemService.loadedInitialData = false; // Reset en caso de error
        });
    }

    loadParameters() {
        return new Promise((resolve) => {
            this.appService.get(this.urlParams).then((response : ServiceResponse) =>
            {
              this.systemService.parameters = response.data;
              resolve(1);
            });
        });
    }

    loadPermissions() {
        return new Promise((resolve) => {

            this.appService.get(this.urlUsuario + '/permissions').then((response: ServiceResponse) =>
                {

                    // this.router.navigateByUrl("/app/home");

                    let data = response.data;

                    this.systemService.permissions = data.permissions;

                    this.systemService.setUser(data.user);
                    this.systemService.user.maskedUser = data.maskUser;
                    this.systemService.user.currentUser = data.currentUser;
                    this.systemService.menuOptions = data.menuOptions;
                    this.systemService.screenDetails = data.screenDetails;

                    this.systemService.getHomeMenu();

                    this.systemService.loadedPermissions = true;

                    if (this.systemService.user.maskedUser) {
                        if (localStorage.getItem(this.systemService.localStoreVariable.UsuarioEnmascarado) === '1') {
                        localStorage.removeItem(this.systemService.localStoreVariable.UsuarioEnmascarado);
                        }
                        else {
                        let opciones = [];

                        opciones.push(new DialogOption({ title: this.systemService.user.currentUser?.person?.name, value: 1, type: OptionEnum.Negative }));
                        opciones.push(new DialogOption({ title: this.systemService.user.maskedUser?.person?.name, value: 2, type: OptionEnum.Positive }));
                        const mensaje = this.translate.instant('Perfil.MensajeUsuarioEnmascarado');

                        this.messageService.dialog(DialogEnum.Normal, '', mensaje, opciones).then((response: any) => {

                            if (!response) {
                                this.logOutMask();
                            }

                        });
                        }
                    }

                    if (!this.systemService.user.userType?.permanentSession) {
                        // Configurar tiempos de sesión (valores en minutos desde el backend)
                        // inactivityTime: tiempo de inactividad en minutos
                        // sessionTime: tiempo total de sesión en minutos
                        this.systemService.user.userType!.inactivityTime = this.systemService.user.userType?.inactivityTime! * 60 * 1000; // Convertir a milisegundos
                        this.systemService.user.userType!.sessionTime = this.systemService.user.userType?.sessionTime! * 60; // Convertir a segundos

                        this.appService.startIdleCount.next(1);
                        this.setSessionTimer();
                    }

                    if (this.systemService.user.resetPassword) {
                        this.router.navigateByUrl('/app/change-password');
                    }

                    return resolve(1);
                }
            ).catch(() =>
            {
              this.systemService.menuOptions = [];
              return resolve(0);
            });
        }).catch(() => {
          this.systemService.menuOptions = [];
          //return resolve(0);
        });
    }

    getMenuOptions(menuOptions: any) {
        let newMenuOptions: MenuOption[] = [];

        menuOptions?.forEach((menu: { TipoModulo: any; Ruta: any; Titulo: any; Icono: any; PantallaInicio: any; Abierto: any; Activo: any; TipoIconoId: any; Orden: any; OrdenInicio: any; SubMenu: { TipoModulo: any; Ruta: any; Titulo: any; Icono: any; PantallaInicio: any; Abierto: any; Activo: any; TipoIconoId: any; Orden: any; OrdenInicio: any; }[] | undefined; }) => {
            let menuOption: MenuOption = {
                moduleType: menu.TipoModulo,
                path: menu.Ruta,
                title: menu.Titulo,
                icon: menu.Icono,
                homeScreen: menu.PantallaInicio,
                isOpen: menu.Abierto,
                isActive: menu.Activo,
                subMenu: [],
                order: menu.Orden,
                homeOrder: menu.OrdenInicio,
                function: UserFunctionsEnum.ChangePassword
            }

            if (menu.SubMenu !== undefined) {
                menuOption.subMenu = this.getMenuOptions(menu.SubMenu);
            }

            newMenuOptions.push(menuOption);
        });

        return newMenuOptions;
    }

    // Idioma
    changeLanguage(clave: any) {
        if (this.systemService.selectedLanguage?.keyCode != clave) {
            if (localStorage.getItem(this.systemService.localStoreVariable.JWT)) {
                //this.changeUserLanguage(clave);
                this.systemService.changeLanguage(clave);
            }
            else {
                localStorage.setItem(this.systemService.localStoreVariable.LenguajeCambiado, '1');
                this.systemService.changeLanguage(clave);
            }
        }
    }

    changeUserLanguage(idiomaClave: any) {
        this.appService.post(this.urlUsuario + '/idioma', { Clave: idiomaClave }).then((response : ServiceResponse) => {
          this.systemService.changeLanguage(idiomaClave);
        });
    }

    // Funciones de sesion
    resumeSession() {
        this.appService.put(this.urlSession, null).then((response : ServiceResponse) =>
        {
          // Limpiar la fecha guardada para reiniciar el timer
          localStorage.removeItem(this.systemService.localStoreVariable.SesionValida);
          this.setSessionTimer();

          // Notificar que la sesión se ha renovado (para reiniciar la cuenta regresiva en la UI)
          this.appService.sessionEnded.next(false);
        });
    }

    setSessionTimer() {
        // Validar que existe el usuario y el userType antes de proceder
        if (!this.systemService.user || !this.systemService.user.userType) {
            console.error('Cannot set session timer: user or userType is null');
            return;
        }

        const encodedSessionData = localStorage.getItem(this.systemService.localStoreVariable.SesionValida);

        if (encodedSessionData) {
            // Si existe una fecha guardada codificada, decodificarla para continuar el cronómetro
            try {
                const decodedData = this.appService.decryptString(encodedSessionData);
                this.sesionFin = new Date(decodedData);
            } catch (error) {
                // Si hay error al decodificar, crear nueva fecha
                this.createNewSessionEndTime();
            }
        } else {
            // Si no existe, crear nueva fecha de fin de sesión
            this.createNewSessionEndTime();
        }

        this.startSessionTimer();
    }

    private createNewSessionEndTime() {
        // Validar que existe el usuario y el userType antes de proceder
        if (!this.systemService.user || !this.systemService.user.userType) {
            console.error('Cannot create session end time: user or userType is null');
            return;
        }

        const sessionTimeSeconds = this.systemService.user.userType?.sessionTime || 0;

        if (sessionTimeSeconds <= 0) {
            console.error('Invalid session time, using default 30 minutes');
            // Si no hay sessionTime válido, usar 30 minutos por defecto
            this.sesionFin = new Date();
            this.sesionFin.setSeconds(this.sesionFin.getSeconds() + (30 * 60)); // 30 minutos
        } else {
            this.sesionFin = new Date();
            this.sesionFin.setSeconds(this.sesionFin.getSeconds() + sessionTimeSeconds);
        }

        // Codificar la fecha antes de guardarla en localStorage
        const sessionEndTimeString = this.sesionFin.toISOString();
        const encodedSessionData = this.appService.encryptString(sessionEndTimeString);

        localStorage.setItem(this.systemService.localStoreVariable.SesionValida, encodedSessionData);
    }

    private saveEncodedSessionTime() {
        if (this.sesionFin) {
            const sessionEndTimeString = this.sesionFin.toISOString();
            const encodedSessionData = this.appService.encryptString(sessionEndTimeString);
            localStorage.setItem(this.systemService.localStoreVariable.SesionValida, encodedSessionData);
        }
    }

    startSessionTimer() {
        this.timerSesion = setInterval(() => {
            var tiempo = this.timeDifference(this.sesionFin);

            if (tiempo == 0) {
                clearInterval(this.timerSesion);
                this.timerSesion = null;
                this.appService.sessionEnded.next(true);
            }

        }, 1000);
    }

    timeDifference(termina: any) {
        var dateEntered = termina;
        var now = new Date();
        var difference = dateEntered.getTime() - now.getTime();

        if (difference <= 0) {
            clearInterval(this.timerSesion);
            this.timerSesion = null;

            return 0;
        }
        else {
            var segundos = Math.ceil(difference / 1000);

            return segundos;
        }
    }

    setAPI()
    {
        var api = localStorage.getItem(this.systemService.localStoreVariable.AOT) ? ApiEnum.Rest : ApiEnum.Security;
        this.configService.setURLBase(api);

        this.configService.config.securityAPI = this.appService.base64ToString(this.configService.config.securityAPI);

        // Validar que servicesAPI exista antes de desencriptar
        if (this.configService.config.servicesAPI) {
            this.configService.config.servicesAPI = this.appService.base64ToString(this.configService.config.servicesAPI);
        }

        this.setUserUrl(api);
    }

    setUserUrl(api: ApiEnum) {

        if (api == ApiEnum.Rest) {
            this.urlUsuario = this.configService.config.securityAPI + 'users';
            this.urlUsuarioAdmin = this.configService.config.securityAPI + 'users';
            this.urlSession = this.configService.config.securityAPI + 'sessions';
        } else {
            this.urlUsuario = this.configService.config.securityAPI + 'authentication';
            this.urlUsuarioAdmin = this.configService.config.securityAPI + 'users';
        }

    }
}
